import '@digital-realty/ix-icon-button/ix-icon-button.js';
import '@digital-realty/ix-checkbox/ix-checkbox.js';
import { html, LitElement, nothing, TemplateResult } from 'lit';
import { property, state, query } from 'lit/decorators.js';

import { IIxTreeNode } from './IIxTreeNode.js';
import { IxTreeStyles } from './ix-tree-styles.js';
import { Appearance } from './IxTree.js';
import { IxTreeTestIds as TestIds } from './constants/IxTreeTestIds.js';

export class IxTreeNode extends LitElement {
  static readonly styles = [IxTreeStyles];

  @property({ type: Boolean }) isActiveTreeNode: boolean = false;

  @property({ type: Boolean }) isExpanded: boolean = false;

  @property({ type: Boolean }) isSelected: boolean = false;

  @property({ type: Number }) level: number = 0;

  @property({ type: Object }) node!: IIxTreeNode;

  @property({ type: Boolean }) checkboxEnabled: boolean = false;

  @property({ type: String }) appearance: Appearance = 'default';

  @property({ type: Boolean }) hasIcon: boolean = false;

  @property({ type: Boolean }) removeChevron: boolean = false;

  @property({ type: Boolean }) allowMultiLine: boolean = false;

  @property({ type: Boolean }) scrollToActive: boolean = false;

  @property({ type: Boolean }) disabled: boolean = false;

  @property({ type: Boolean }) selectNodeOnClick: boolean = false;

  @state() private _hasScrolledToActive: boolean = false;

  @query('.tree-node-container') private _treeNodeContainer!: HTMLElement;

  private hasChildren() {
    return this.node.children !== undefined && this.node.children.length > 0;
  }

  private getChevron(): TemplateResult<1> | typeof nothing {
    const icon = this.isExpanded ? 'arrow_drop_down' : 'arrow_right';
    return html`<div
      style=${`${this.hasChildren() ? '' : 'visibility:hidden'}`}
    >
      <ix-icon-button
        data-testid="${TestIds.CHEVRON_TOGGLE(this.node.id)}"
        @click=${() => this.onNodeToggle(this.node)}
        icon=${icon}
        class="chevron"
        test-id="tree-node-expand-toggle"
      >
      </ix-icon-button>
    </div>`;
  }

  private renderCheckbox() {
    if (this.node.fixed) {
      return html` <ix-icon class="inherited">check</ix-icon>`;
    }

    if (this.node.disabled) {
      return html` <ix-icon class="inherited">arrow_drop_up</ix-icon>`;
    }

    return html`<ix-checkbox
      data-testid="${TestIds.CHECKBOX(this.node.id)}"
      @click=${() => this.onNodeCheck(this.node)}
      label=""
      class="item-checkbox"
      .checked=${this.node.checked}
      .indeterminate=${this.node.indeterminate}
    ></ix-checkbox>`;
  }

  private onNodeClick(node: IIxTreeNode): void {
    const event = new CustomEvent('on-tree-node-click', {
      detail: {
        message: {
          node,
          id: node.id,
          label: node.label,
          parentId: node.parentId,
        },
      },
    });

    this.dispatchEvent(event);
  }

  private onNodeToggle(node: IIxTreeNode): void {
    const event = new CustomEvent('on-tree-node-toggle', {
      detail: {
        message: {
          node,
          id: node.id,
          label: node.label,
          parentId: node.parentId,
        },
      },
    });

    this.dispatchEvent(event);
  }

  private onNodeCheck(node: IIxTreeNode): void {
    /* eslint-disable no-param-reassign */
    if (node.checked) {
      node.checked = false;
      node.indeterminate = false;
    } else {
      node.checked = true;
      node.indeterminate = false;
    }
    const event = new CustomEvent('on-tree-node-check', {
      detail: {
        message: {
          node,
          id: node.id,
        },
      },
    });

    this.dispatchEvent(event);
  }

  private handleNodeClick(node: IIxTreeNode): void {
    if (this.disabled || node.disabled) {
      return;
    }

    if (this.selectNodeOnClick) {
      this.onNodeCheck(node);
      return;
    }

    if (this.checkboxEnabled) {
      this.onNodeToggle(node);
    } else {
      this.onNodeClick(node);
    }
  }

  protected render(): TemplateResult<1> {
    const selectedClass = this.isSelected ? 'selected' : '';
    const isDisabled = this.disabled || this.node.disabled;
    const disabledClass = isDisabled ? 'disabled' : '';
    const cursorClass = isDisabled ? 'cursor-default' : 'cursor-pointer';
    const icon = this.node.icon ? this.node.icon : 'account_box';

    return html`<div
      class="tree-node-container"
      ?active-tree-node=${this.isActiveTreeNode}
      aria-label=${this.node.label}
      aria-expanded=${!!this.node.expanded}
      aria-selected=${this.isSelected}
      aria-disabled=${isDisabled}
      data-id=${this.node.id}
      role="treeitem"
      data-testid="${TestIds.CONTAINER(this.node.id)}"
    >
      <div
        class="wrapper ${selectedClass} ${cursorClass} ${disabledClass}"
        style="padding-left: calc(var(--ix-tree-icon-size) * ${this.level})"
      >
        ${!this.removeChevron ? this.getChevron() : nothing}
        ${this.checkboxEnabled ? this.renderCheckbox() : nothing}
        ${this.hasIcon
          ? html`<ix-icon class="icon" data-testid=${TestIds.ICON(this.node.id)}
              >${icon}</ix-icon
            >`
          : nothing}
        <div
          data-testid="${TestIds.LABEL(this.node.id)}"
          class="label ${this.allowMultiLine
            ? 'label-multi-line'
            : 'label-single-line'}"
          @click=${() => this.handleNodeClick(this.node)}
          @keyup=${() => this.handleNodeClick(this.node)}
        >
          ${this.node.label}
        </div>
      </div>
      <slot></slot>
    </div>`;
  }

  protected updated(): void {
    if (this.scrollToActive && !this._hasScrolledToActive) {
      const el = this.shadowRoot?.querySelector('[active-tree-node]');
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      this._hasScrolledToActive = true;
    }
    if (this.isSelected) {
      this.dispatchEvent(
        new CustomEvent('selected-node-ref', {
          detail: this._treeNodeContainer,
          bubbles: true,
          composed: true,
        })
      );
    }
  }
}
